import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CircularProgress } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { FlexBox } from '../../../global/FlexBox';
import { Progress } from '@backstage/core-components';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DiamDrawer, DiamDrawerCancelButton } from '../DiamDrawer/DiamDrawer';
import { EDIT_TEAM_DRAWER_ID } from '../../constants';
import { useMinimizedDrawers } from '../../context/MinimizedDrawersContext';
import { Team } from '../TeamsGrid/types';
import { TeamFormFields } from '../CreateTeamDrawer/TeamFormFields';
import { useCreateTeamApplicationOptions } from '../CreateTeamDrawer/useCreateTeamApplicationOptions';
import { useCreateTeamModifiers } from '../CreateTeamDrawer/useCreateTeamModifiers';
import { useParentTeamTree } from '../CreateTeamDrawer/useParentTeamTree';
import { buildCreateTeamValidationSchema } from '../CreateTeamDrawer/validationSchema';
import { findTreeNodeLabel } from '../CreateTeamDrawer/treeUtils';
import { CreateTeamFormData } from '../CreateTeamDrawer/types';
import {
  buildCreateTeamSubmitPayload,
  deserializeCreateTeamFormData,
  getDefaultCreateTeamFormData,
} from '../CreateTeamDrawer/formUtils';
import { useTeam } from '../TeamDetails/useTeam';
import { useTeamApplicationContexts } from '../TeamDetails/useTeamApplicationContexts';
import { getEditTeamFormDataFromApi } from './formUtils';
import { useUpdateTeam } from './useUpdateTeam';

interface EditTeamDrawerProps {
  team: Team | null;
  isOpen: boolean;
  onClose: () => void;
  onOpen: (team: Team) => void;
}

function isEditTeamDrawerRestore(id: string | undefined): boolean {
  return id === EDIT_TEAM_DRAWER_ID;
}

interface EditTeamDrawerContextValue {
  openEditTeam: (team: Team) => void;
}

const EditTeamDrawerContext = createContext<
  EditTeamDrawerContextValue | undefined
>(undefined);

export function useEditTeamDrawer() {
  const context = useContext(EditTeamDrawerContext);
  if (!context) {
    throw new Error(
      'useEditTeamDrawer must be used within EditTeamDrawerProvider',
    );
  }
  return context;
}

export function EditTeamDrawerProvider({ children }: PropsWithChildren<{}>) {
  const [team, setTeam] = useState<Team | null>(null);
  const value = useMemo(() => ({ openEditTeam: setTeam }), []);

  return (
    <EditTeamDrawerContext.Provider value={value}>
      {children}
      <EditTeamDrawer
        team={team}
        isOpen={Boolean(team)}
        onOpen={setTeam}
        onClose={() => setTeam(null)}
      />
    </EditTeamDrawerContext.Provider>
  );
}

export function EditTeamDrawer({
  team,
  isOpen,
  onClose,
  onOpen,
}: EditTeamDrawerProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { minimizeDrawer, lastRestoredItem, clearLastRestoredItem } =
    useMinimizedDrawers();
  const populatedTeamId = useRef<string | null>(null);
  const teamId = team?.id;

  const {
    accountOptions,
    locationTree,
    modifierFieldsConfig,
    isLoading: areModifiersLoading,
    isError: areModifiersError,
    error: modifiersError,
  } = useCreateTeamModifiers(isOpen);
  const {
    applicationOptions,
    isLoading: areApplicationsLoading,
    isError: areApplicationsError,
    error: applicationsError,
  } = useCreateTeamApplicationOptions(isOpen);
  const {
    parentTeamTree,
    isLoading: isParentTeamLoading,
    isError: isParentTeamError,
    error: parentTeamError,
  } = useParentTeamTree(isOpen);
  const teamDetailsQuery = useTeam(isOpen ? teamId : undefined);
  const applicationContextsQuery = useTeamApplicationContexts(
    isOpen ? teamId : undefined,
  );

  const { mutate: updateTeam, isPending } = useUpdateTeam();

  const defaultValues = useMemo(
    () => getDefaultCreateTeamFormData(modifierFieldsConfig),
    [modifierFieldsConfig],
  );

  const validationSchema = useMemo(
    () => buildCreateTeamValidationSchema(modifierFieldsConfig),
    [modifierFieldsConfig],
  );

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    clearErrors,
    watch,
    setValue,
  } = useForm<CreateTeamFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const selectedApplications = watch('applications');
  const isFormDataLoading =
    teamDetailsQuery.isPending ||
    applicationContextsQuery.isPending ||
    areModifiersLoading;

  useEffect(() => {
    if (isParentTeamLoading) {
      return;
    }

    const currentParentTeamId = getValues('parentTeamId');
    if (
      currentParentTeamId &&
      !findTreeNodeLabel(parentTeamTree, currentParentTeamId)
    ) {
      setValue('parentTeamId', '');
    }
  }, [getValues, isParentTeamLoading, parentTeamTree, setValue]);

  useEffect(() => {
    if (!isOpen || !areApplicationsError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(applicationsError, 'Failed to load applications'),
      { variant: 'error' },
    );
  }, [applicationsError, areApplicationsError, enqueueSnackbar, isOpen]);

  useEffect(() => {
    if (!isOpen || !isParentTeamError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(parentTeamError, 'Failed to load parent teams'),
      { variant: 'error' },
    );
  }, [enqueueSnackbar, isOpen, isParentTeamError, parentTeamError]);

  useEffect(() => {
    if (!isOpen || !areModifiersError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(modifiersError, 'Failed to load account and location'),
      { variant: 'error' },
    );
  }, [areModifiersError, enqueueSnackbar, isOpen, modifiersError]);

  useEffect(() => {
    if (!isOpen || !teamDetailsQuery.isError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(teamDetailsQuery.error, 'Failed to load team details'),
      { variant: 'error' },
    );
  }, [
    enqueueSnackbar,
    isOpen,
    teamDetailsQuery.error,
    teamDetailsQuery.isError,
  ]);

  useEffect(() => {
    if (!isOpen || !applicationContextsQuery.isError) {
      return;
    }

    enqueueSnackbar(
      getApiErrorMessage(
        applicationContextsQuery.error,
        'Failed to load team applications',
      ),
      { variant: 'error' },
    );
  }, [
    applicationContextsQuery.error,
    applicationContextsQuery.isError,
    enqueueSnackbar,
    isOpen,
  ]);

  const handleDrawerClose = () => {
    populatedTeamId.current = null;
    reset(defaultValues);
    onClose();
  };

  useEffect(() => {
    if (!isEditTeamDrawerRestore(lastRestoredItem?.id)) {
      return;
    }

    const stored = lastRestoredItem?.data as
      | (CreateTeamFormData & { team?: Team })
      | undefined;
    if (!stored?.team) {
      clearLastRestoredItem();
      return;
    }

    populatedTeamId.current = stored.team.id;
    reset(deserializeCreateTeamFormData(stored, modifierFieldsConfig));
    onOpen(stored.team);
    clearLastRestoredItem();
  }, [
    clearLastRestoredItem,
    lastRestoredItem,
    modifierFieldsConfig,
    onOpen,
    reset,
  ]);

  useEffect(() => {
    if (!isOpen || !teamId || !teamDetailsQuery.data) {
      return;
    }

    if (!applicationContextsQuery.isSuccess) {
      return;
    }

    if (areModifiersLoading) {
      return;
    }

    if (populatedTeamId.current === teamId) {
      return;
    }

    populatedTeamId.current = teamId;
    const applicationContextIds = (
      applicationContextsQuery.data.elements ?? []
    ).map(context => context.id);
    reset(
      getEditTeamFormDataFromApi(
        teamDetailsQuery.data,
        applicationContextIds,
        modifierFieldsConfig,
      ),
    );
  }, [
    applicationContextsQuery.data,
    applicationContextsQuery.isSuccess,
    areModifiersLoading,
    isOpen,
    modifierFieldsConfig,
    reset,
    teamDetailsQuery.data,
    teamId,
  ]);

  const onSubmit = (data: CreateTeamFormData) => {
    if (!team) {
      return;
    }

    const payload = {
      id: team.id,
      ...buildCreateTeamSubmitPayload(data, modifierFieldsConfig),
    };

    updateTeam(payload, {
      onSuccess: () => {
        handleDrawerClose();
        enqueueSnackbar('Team successfully updated', { variant: 'success' });
      },
      onError: error => {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to update team'), {
          variant: 'error',
        });
      },
    });
  };

  const handleSave = () => {
    clearErrors();
    handleSubmit(onSubmit)();
  };

  const handleHide = () => {
    if (!team) {
      return;
    }

    minimizeDrawer(EDIT_TEAM_DRAWER_ID, 'Edit Team', {
      ...getValues(),
      team,
    });
    handleDrawerClose();
  };

  return (
    <DiamDrawer
      title="Edit Team"
      hasHide
      isOpen={isOpen}
      handleDrawerClose={handleDrawerClose}
      confirmOnClose
      confirmTitle="Are you sure you want to cancel?"
      confirmContent="All the details provided will be lost."
      confirmButtonText="Cancel editing Team"
      cancelButtonText="Dismiss"
      handleHide={handleHide}
    >
      {isFormDataLoading ? (
        <Progress />
      ) : (
        <TeamFormFields
          control={control}
          isPending={isPending}
          applicationOptions={applicationOptions}
          areApplicationsLoading={areApplicationsLoading}
          parentTeamTree={parentTeamTree}
          isParentTeamLoading={isParentTeamLoading}
          isParentTeamReadOnly
          accountOptions={accountOptions}
          locationTree={locationTree}
          areModifiersLoading={areModifiersLoading}
          selectedApplications={selectedApplications}
        />
      )}

      <FlexBox sx={{ justifyContent: 'flex-end', gridColumnGap: 24 }}>
        <DiamDrawerCancelButton disabled={isPending || isFormDataLoading} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isPending || isFormDataLoading || !team}
          startIcon={
            isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          Save
        </Button>
      </FlexBox>
    </DiamDrawer>
  );
}
